import { EmployeeDTO, EmployeeDuplicatePeerDTO } from 'probonio-shared-ui/api';

/** Tab value in the url (?state=duplicates); kept here so alert and tabs do not import each other. */
export const DUPLICATES_TAB_VALUE = 'duplicates';

export type EmployeeDuplicateField = 'mail' | 'employeeNumber';
/**
 * CLAIMABLE: the incoming value is still free - confirming assigns it to this employee.
 * MISMATCH: nobody competes, but the incoming value would replace the one the employee has today.
 * CONFLICT: somebody else already holds the value - a new one has to be entered.
 */
export type EmployeeDuplicateState = 'CLAIMABLE' | 'MISMATCH' | 'CONFLICT';

export interface EmployeeDuplicate {
  field: EmployeeDuplicateField;
  state: EmployeeDuplicateState;
  /** Whether this employee can still take the account service value - drives the confirm checkbox. */
  claimable: boolean;
  /** Account service value behind the duplicate - never empty. */
  value: string;
  /** Other employees carrying the same value. Only delivered by the detail endpoint. */
  peers: EmployeeDuplicatePeerDTO[];
}

export interface EmployeeDuplicates {
  mail?: EmployeeDuplicate;
  employeeNumber?: EmployeeDuplicate;
  /** state CLAIMABLE - shown in warning colors, confirming adopts the incoming value */
  warnings: EmployeeDuplicate[];
  /** state MISMATCH - shown in warning colors, confirming keeps the current value */
  mismatches: EmployeeDuplicate[];
  /** state CONFLICT - shown in error colors */
  conflicts: EmployeeDuplicate[];
  hasAny: boolean;
}

export const ACCOUNT_SERVICE_FIELD: Record<EmployeeDuplicateField, keyof EmployeeDTO> = {
  mail: 'accountServiceMail',
  employeeNumber: 'accountServiceEmployeeNumber',
};

const DUPLICATE_STATE_FIELD: Record<EmployeeDuplicateField, keyof EmployeeDTO> = {
  mail: 'mailDuplicateState',
  employeeNumber: 'employeeNumberDuplicateState',
};

const DUPLICATE_PEERS_FIELD: Record<EmployeeDuplicateField, keyof EmployeeDTO> = {
  mail: 'mailDuplicatePeers',
  employeeNumber: 'employeeNumberDuplicatePeers',
};

const DUPLICATE_CLAIMABLE_FIELD: Record<EmployeeDuplicateField, keyof EmployeeDTO> = {
  mail: 'mailDuplicateClaimable',
  employeeNumber: 'employeeNumberDuplicateClaimable',
};

/** Stable reference for the common case, so consumers can compare without memoizing. */
const NO_DUPLICATES: EmployeeDuplicates = { warnings: [], mismatches: [], conflicts: [], hasAny: false };

export function mapEmployeeDuplicate(employee: EmployeeDTO, field: EmployeeDuplicateField): EmployeeDuplicate | undefined {
  const state = employee[DUPLICATE_STATE_FIELD[field]];
  if (state !== 'CLAIMABLE' && state !== 'MISMATCH' && state !== 'CONFLICT') {
    return undefined;
  }
  const value = employee[ACCOUNT_SERVICE_FIELD[field]] as string | undefined;
  if (!value) {
    return undefined;
  }
  return {
    field,
    state,
    claimable: !!employee[DUPLICATE_CLAIMABLE_FIELD[field]],
    value,
    peers: (employee[DUPLICATE_PEERS_FIELD[field]] as EmployeeDuplicatePeerDTO[] | undefined) ?? [],
  };
}

/**
 * Whether the cell should render through DuplicateValue: either there is a duplicate to mark, or the
 * employee has no own value and the incoming one stands in for it.
 */
export function hasDuplicateDisplay(employee: EmployeeDTO, field: EmployeeDuplicateField): boolean {
  if (mapEmployeeDuplicate(employee, field)) {
    return true;
  }
  return !employee[field] && !!employee[ACCOUNT_SERVICE_FIELD[field]];
}

export function mapEmployeeDuplicates(employee: EmployeeDTO): EmployeeDuplicates {
  const mail = mapEmployeeDuplicate(employee, 'mail');
  const employeeNumber = mapEmployeeDuplicate(employee, 'employeeNumber');

  if (!mail && !employeeNumber) {
    return NO_DUPLICATES;
  }

  const all = [mail, employeeNumber].filter((duplicate): duplicate is EmployeeDuplicate => !!duplicate);
  return {
    mail,
    employeeNumber,
    warnings: all.filter(duplicate => duplicate.state === 'CLAIMABLE'),
    mismatches: all.filter(duplicate => duplicate.state === 'MISMATCH'),
    conflicts: all.filter(duplicate => duplicate.state === 'CONFLICT'),
    hasAny: true,
  };
}

/**
 * Everyone else in the tenant carrying one of this employees account service values. As long as such
 * a peer exists the account service rejects every update of a synced field - even when this employee
 * itself already holds its values and looks unremarkable.
 */
export function getAccountServicePeers(employee: EmployeeDTO): EmployeeDuplicatePeerDTO[] {
  // the same peer can collide on both fields, so it must not be listed (and keyed) twice
  const byId = new Map<string, EmployeeDuplicatePeerDTO>();
  [...(employee.mailDuplicatePeers ?? []), ...(employee.employeeNumberDuplicatePeers ?? [])].forEach(peer => byId.set(peer.id, peer));
  return [...byId.values()];
}
